import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieGrid from "@/components/feature/MovieGrid";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { themeCollections } from "@/mocks/movies";
import { filterMovies } from "@/utils/movieUtils";
import type { MovieType } from "@/types/movie";
import FilterBar, { type FilterState } from "./components/FilterBar";

const PAGE_SIZE = 10;

const defaultFilter: FilterState = { tags: [], yearFrom: null, yearTo: null, sort: "hot" };

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = (searchParams.get("type") as MovieType) || "movie";
  const themeParam = searchParams.get("theme") || undefined;

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [page, setPage] = useState(1);
  const [mobilePage, setMobilePage] = useState(1);

  const { favorites, toggleFavorite } = useFavorites();
  const theme = themeCollections.find((t) => t.key === themeParam);

  const typeLabel = typeParam === "tv" ? "伦理电视剧" : "伦理电影";

  usePageMeta({
    title: `${theme ? theme.name + " - " : ""}${typeLabel} - 伦理影院`,
    description: `${typeLabel}分类页，汇集${theme ? theme.name + "专题的" : ""}伦理题材影视作品，支持按类型标签、年份与热度筛选，快速找到想看的伦理影视剧。`,
    keywords: `伦理影院,${typeLabel},伦理影视,${theme ? theme.name + "," : ""}影视筛选`,
  });

  const results = useMemo(
    () => filterMovies({ type: typeParam, theme: themeParam, ...filter }),
    [typeParam, themeParam, filter],
  );

  useEffect(() => {
    setPage(1);
    setMobilePage(1);
  }, [typeParam, themeParam, filter]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pagedMovies = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const mobileMovies = results.slice(0, mobilePage * PAGE_SIZE);

  const switchType = (next: MovieType) => {
    const params: Record<string, string> = { type: next };
    if (themeParam) params.theme = themeParam;
    setSearchParams(params);
  };

  const resetFilter = () => setFilter(defaultFilter);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8">
        <nav aria-label="面包屑" className="pt-6 text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
            </li>
            <li>/</li>
            <li className="text-foreground-800">{theme ? theme.name : typeLabel}</li>
          </ol>
        </nav>

        <div className="mt-5">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">
            {theme ? theme.name : typeLabel}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {theme
              ? `「${theme.name}」专题合集：${theme.subtitle}。共收录 ${results.length} 部相关影视作品。`
              : `浏览全部${typeLabel}，涵盖${typeParam === "tv" ? "都市、家庭、社会等" : "悬疑、家庭、情感等"}多种伦理题材，当前共 ${results.length} 部作品，可按标签、年份与热度进一步筛选。`}
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-200/70 bg-background-100 p-1">
            <button
              type="button"
              onClick={() => switchType("movie")}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                typeParam === "movie" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              伦理电影
            </button>
            <button
              type="button"
              onClick={() => switchType("tv")}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                typeParam === "tv" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              伦理电视剧
            </button>
          </div>
          {themeParam && (
            <Link
              to={`/category?type=${typeParam}`}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300/60 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
            >
              <i className="ri-close-line"></i> 退出专题
            </Link>
          )}
        </div>

        <div className="mt-6">
          <FilterBar value={filter} onChange={setFilter} onReset={resetFilter} />
        </div>

        {results.length === 0 ? (
          <div className="mt-16 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-900">未找到符合条件的影视资源</h2>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">
              试试减少筛选条件，或重置后重新浏览全部{typeLabel}。
            </p>
            <button
              type="button"
              onClick={resetFilter}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i> 重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="mt-8 hidden md:block">
              <MovieGrid movies={pagedMovies} favorites={favorites} onToggleFavorite={toggleFavorite} />
            </div>
            <div className="mt-8 md:hidden">
              <MovieGrid movies={mobileMovies} favorites={favorites} onToggleFavorite={toggleFavorite} />
              {mobilePage < totalPages && (
                <button
                  type="button"
                  onClick={() => setMobilePage((p) => p + 1)}
                  className="mt-6 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 bg-background-50 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600"
                >
                  <i className="ri-add-line"></i> 加载更多
                </button>
              )}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 hidden items-center justify-center gap-2 md:flex">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/60 text-foreground-700 transition-colors hover:border-accent-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`flex h-9 min-w-9 items-center justify-center rounded-md border px-3 text-sm transition-colors ${
                      n === page
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-300/60 text-foreground-700 hover:border-accent-300"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/60 text-foreground-700 transition-colors hover:border-accent-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}